import useEmblaCarousel from 'embla-carousel-react';
import * as React from 'react';

/**
 * The report nav's entry track on Embla, the design system's library for a
 * horizontally scrolled row: drag or swipe moves it freely, and the active
 * entry is brought into view whenever the scroll-spy moves on, so on a phone
 * the reader always sees where they are. The ends that hide entries are
 * marked for the CSS edge fade. Embla re-measures itself when entries or the
 * track size change, so a new item set needs no reInit here.
 */
export function useReportNavTrack(
  activeId: string | undefined,
): (node: HTMLElement | null) => void {
  const [viewportRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
    // A half-hidden active pill counts as out of view, so it is brought in whole.
    inViewThreshold: 1,
  });

  React.useEffect(() => {
    if (!embla) return undefined;
    const markEnds = (): void => {
      const root = embla.rootNode();
      const scrolls = embla.scrollSnapList().length > 1;
      const progress = embla.scrollProgress();
      root.dataset.fadeStart = String(scrolls && progress > 0.001);
      root.dataset.fadeEnd = String(scrolls && progress < 0.999);
    };
    markEnds();
    embla.on('scroll', markEnds);
    embla.on('reInit', markEnds);
    embla.on('resize', markEnds);
    return () => {
      embla.off('scroll', markEnds);
      embla.off('reInit', markEnds);
      embla.off('resize', markEnds);
    };
  }, [embla]);

  React.useEffect(() => {
    if (!embla || !activeId) return;
    const index = embla
      .slideNodes()
      .findIndex((node) => node.getAttribute('aria-current') === 'page');
    if (index < 0 || embla.slidesInView().includes(index)) return;
    embla.scrollTo(Math.min(index, embla.scrollSnapList().length - 1));
  }, [embla, activeId]);

  return viewportRef;
}
